export type TimeOfDayPhase = 'start_of_day' | 'mid_day' | 'end_of_day' | 'off_hours';

export interface TimeOfDayBoundary {
  hour: number;
  minute: number;
}

export interface TimeOfDayBoundaries {
  startOfDay: TimeOfDayBoundary;
  midDay: TimeOfDayBoundary;
  endOfDay: TimeOfDayBoundary;
  offHoursEnd: TimeOfDayBoundary;
}

/** v1 product defaults — local wall clock in user timezone. */
export const DEFAULT_TIME_OF_DAY_BOUNDARIES: TimeOfDayBoundaries = {
  startOfDay: { hour: 5, minute: 0 },
  midDay: { hour: 11, minute: 0 },
  endOfDay: { hour: 17, minute: 0 },
  offHoursEnd: { hour: 21, minute: 0 },
};

export function resolveUserTimezone(profileTimezone?: string | null): string {
  const trimmed = profileTimezone?.trim();
  if (trimmed) {
    return trimmed;
  }

  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function getLocalTimeParts(
  date: Date,
  timeZone: string
): { hour: number; minute: number } {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return { hour, minute };
}

function toMinutes({ hour, minute }: TimeOfDayBoundary): number {
  return hour * 60 + minute;
}

export function getTimeOfDayPhase(
  date: Date,
  timeZone: string,
  boundaries: TimeOfDayBoundaries = DEFAULT_TIME_OF_DAY_BOUNDARIES
): TimeOfDayPhase {
  const { hour, minute } = getLocalTimeParts(date, timeZone);
  const nowMinutes = hour * 60 + minute;
  const start = toMinutes(boundaries.startOfDay);
  const mid = toMinutes(boundaries.midDay);
  const end = toMinutes(boundaries.endOfDay);
  const offEnd = toMinutes(boundaries.offHoursEnd);

  if (nowMinutes >= start && nowMinutes < mid) {
    return 'start_of_day';
  }
  if (nowMinutes >= mid && nowMinutes < end) {
    return 'mid_day';
  }
  if (nowMinutes >= end && nowMinutes < offEnd) {
    return 'end_of_day';
  }
  return 'off_hours';
}

export function isWithinWindow(
  date: Date,
  timeZone: string,
  windowStart: TimeOfDayBoundary,
  windowEnd: TimeOfDayBoundary
): boolean {
  const { hour, minute } = getLocalTimeParts(date, timeZone);
  const nowMinutes = hour * 60 + minute;
  const start = toMinutes(windowStart);
  const end = toMinutes(windowEnd);

  if (start <= end) {
    return nowMinutes >= start && nowMinutes < end;
  }
  return nowMinutes >= start || nowMinutes < end;
}
